import type { NotificationJobCompanyData } from '@tabbio/types';

export interface NotificationCompanyIdentity {
  name: string | null;
  logo: string | null;
  location: string | null;
  /** Whole percent (0-100). Null when the payload carries no usable score. */
  matchPercent: number | null;
}

const EMPTY_IDENTITY: NotificationCompanyIdentity = {
  name: null,
  logo: null,
  location: null,
  matchPercent: null,
};

/**
 * Monogram tile fills. Kept dark enough to carry white text at every step, and
 * deliberately muted: the tile stands in for a real logo, so it should read as
 * an employer mark rather than as a colour-coded status.
 */
const MONOGRAM_COLORS = [
  '#2f5d50',
  '#2b4c7e',
  '#5b3a78',
  '#7a3f3f',
  '#6b4a1f',
  '#3f5a2f',
  '#1f5f63',
  '#6d3a5d',
  '#3c4a6b',
  '#7a4a2a',
] as const;

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Catalog match scores are fractions (0-1). A value outside that range is a
 * payload shape we do not understand, so it is dropped rather than rendered as
 * a nonsense percentage.
 */
function readMatchPercent(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value <= 0 || value > 1) return null;
  return Math.round(value * 100);
}

/**
 * Reads the company identity a job notification carries on `data`. Every field
 * is optional: notifications created before the payload contract landed - and
 * every non-job type - return the empty identity, and the row falls back to its
 * generic type icon.
 */
export function readNotificationCompany(
  data: unknown,
): NotificationCompanyIdentity {
  if (!data || typeof data !== 'object') return EMPTY_IDENTITY;

  const payload = data as NotificationJobCompanyData;
  return {
    name: readString(payload.companyName),
    logo: readString(payload.companyLogo),
    location: readString(payload.companyLocation),
    matchPercent: readMatchPercent(payload.matchScore),
  };
}

/** Up to two initials, used when a company has no logo on file. */
export function getCompanyMonogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? '')
    .join('');

  return initials ? initials.toUpperCase() : '?';
}

/**
 * Stable fill per company name, so the same employer keeps the same tile colour
 * across sessions and across the list.
 */
export function getCompanyMonogramColor(name: string): string {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }

  return MONOGRAM_COLORS[Math.abs(hash) % MONOGRAM_COLORS.length] as string;
}
